import { z } from "zod";
import { blockSchema } from "../blocks.js";

/**
 * M5's contract surface (PLAN.md "Evals"). Three shapes carry the whole
 * subsystem and all three are stored as jsonb, so they're zod-first here
 * rather than columns: a case's `turns` script, its frozen tool `fixtures`,
 * and its `graders`.
 */

// --- Cases: the multi-turn script ---------------------------------------

/**
 * One scripted visitor turn. Only the visitor's side is scripted — the
 * assistant's replies are what's under test, so recording them here would
 * be recording the answer next to the question (PLAN.md "eval_cases hold
 * multi-turn scripts, because... most real failures happen on turn three").
 */
export const evalTurnSchema = z.object({
  user: z.string().min(1),
});
export type EvalTurn = z.infer<typeof evalTurnSchema>;

// --- Cases: frozen tool fixtures ----------------------------------------

/** Mirrors `CachedProduct` in @cos/ai — a `search_products` fixture replays its hydrated hits so `present_products` can still render for real. */
export const evalFixtureProductSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  priceCents: z.number().int().optional(),
  currency: z.string().optional(),
  imageUrl: z.string().optional(),
  url: z.string().optional(),
  stock: z.enum(["in_stock", "out_of_stock", "unknown"]).optional(),
});
export type EvalFixtureProduct = z.infer<typeof evalFixtureProductSchema>;

/**
 * One frozen tool response. Fixtures for a given tool replay in call order
 * (the last one repeating if the bot calls it more times than were
 * recorded), which is what makes a multi-turn case with two searches
 * reproducible.
 *
 * `products` exists because freezing the *I/O* is the point, not freezing
 * the server's own rendering: replaying a `search_products` response
 * repopulates the turn's product cache, so `present_products` still
 * hydrates cards through the real server-authoritative path instead of
 * having its output frozen too.
 */
export const evalToolFixtureSchema = z.object({
  tool: z.string().min(1),
  output: z.unknown(),
  ok: z.boolean().default(true),
  products: z.array(evalFixtureProductSchema).optional(),
  blocks: z.array(blockSchema).optional(),
  costUsd: z.number().nonnegative().optional(),
});
export type EvalToolFixture = z.infer<typeof evalToolFixtureSchema>;

export const evalFixturesSchema = z.object({
  calls: z.array(evalToolFixtureSchema).default([]),
});
export type EvalFixtures = z.infer<typeof evalFixturesSchema>;

// --- Cases: graders -----------------------------------------------------

/**
 * Three tiers, cheapest first (PLAN.md "Graders"). The deterministic ones
 * cost nothing and catch most regressions, which is why they're the bulk of
 * this union; `llm_judge` and `reference` are the two that spend money.
 */
export const graderSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("tool_called"), label: z.string().optional(), tool: z.string().min(1) }),
  z.object({ kind: z.literal("tool_not_called"), label: z.string().optional(), tool: z.string().min(1) }),
  z.object({ kind: z.literal("contains"), label: z.string().optional(), text: z.string().min(1), caseSensitive: z.boolean().default(false) }),
  z.object({ kind: z.literal("not_contains"), label: z.string().optional(), text: z.string().min(1), caseSensitive: z.boolean().default(false) }),
  z.object({ kind: z.literal("regex"), label: z.string().optional(), pattern: z.string().min(1), flags: z.string().max(8).default("i") }),
  z.object({ kind: z.literal("presented_product_ids"), label: z.string().optional(), ids: z.array(z.string().min(1)).min(1) }),
  z.object({ kind: z.literal("escalated"), label: z.string().optional(), expected: z.boolean() }),
  z.object({ kind: z.literal("order_drafted"), label: z.string().optional(), expected: z.boolean() }),
  z.object({
    kind: z.literal("blocks_include"),
    label: z.string().optional(),
    blockType: z.enum(["text", "products", "actions", "links", "quick_replies", "order_confirm", "form", "notice", "followup"]),
  }),
  z.object({ kind: z.literal("cost_below"), label: z.string().optional(), maxUsd: z.number().positive() }),
  z.object({ kind: z.literal("latency_below"), label: z.string().optional(), maxMs: z.number().int().positive() }),
  z.object({ kind: z.literal("llm_judge"), label: z.string().optional(), rubric: z.string().min(1), threshold: z.number().min(0).max(1).default(0.7) }),
  z.object({ kind: z.literal("reference"), label: z.string().optional(), expected: z.string().min(1), threshold: z.number().min(0).max(1).default(0.7) }),
]);
export type Grader = z.infer<typeof graderSchema>;
export type GraderKind = Grader["kind"];

export const graderResultSchema = z.object({
  kind: z.string(),
  label: z.string().optional(),
  passed: z.boolean(),
  /** 0..1 for judge/reference graders; absent for deterministic ones, which are pass/fail by construction. */
  score: z.number().optional(),
  detail: z.string().optional(),
  /** "A failure you can't explain is a failure nobody acts on" (PLAN.md "Graders") — the judge's own words, stored per result. */
  reasoning: z.string().optional(),
  costUsd: z.string().optional(),
});
export type GraderResult = z.infer<typeof graderResultSchema>;

// --- The graded artifact ------------------------------------------------

export const evalToolCallRecordSchema = z.object({
  name: z.string(),
  args: z.unknown(),
  result: z.unknown(),
  latencyMs: z.number(),
  costUsd: z.string().optional(),
});
export type EvalToolCallRecord = z.infer<typeof evalToolCallRecordSchema>;

export const evalTranscriptTurnSchema = z.object({
  user: z.string(),
  assistant: z
    .object({
      content: z.string().nullable(),
      blocks: z.array(blockSchema).nullable(),
      model: z.string().optional(),
    })
    .nullable(),
  outcome: z.enum(["completed", "blocked", "aborted", "error"]),
});
export type EvalTranscriptTurn = z.infer<typeof evalTranscriptTurnSchema>;

/** Everything a grader is allowed to look at — one sample's full run of a case. */
export const evalTranscriptSchema = z.object({
  turns: z.array(evalTranscriptTurnSchema),
  toolCalls: z.array(evalToolCallRecordSchema),
  tokensIn: z.number(),
  tokensOut: z.number(),
  costUsd: z.number(),
  latencyMs: z.number(),
});
export type EvalTranscript = z.infer<typeof evalTranscriptSchema>;

// --- DTOs ---------------------------------------------------------------

export const evalSuiteDtoSchema = z.object({
  id: z.string().uuid(),
  botId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  createdAt: z.string(),
});
export type EvalSuiteDto = z.infer<typeof evalSuiteDtoSchema>;

export const evalCaseDtoSchema = z.object({
  id: z.string().uuid(),
  suiteId: z.string().uuid(),
  /** Stable identity across edits — `id` changes on every edit, `caseKey` doesn't (PLAN.md "editing a case creates a new version"). */
  caseKey: z.string().uuid(),
  version: z.number().int(),
  name: z.string(),
  tags: z.array(z.string()),
  turns: z.array(evalTurnSchema),
  fixtures: evalFixturesSchema,
  graders: z.array(graderSchema),
  weight: z.number(),
  useLiveTools: z.boolean(),
  seededFromConversationId: z.string().uuid().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EvalCaseDto = z.infer<typeof evalCaseDtoSchema>;

export const evalRunStatusSchema = z.enum(["queued", "running", "completed", "failed"]);
export type EvalRunStatus = z.infer<typeof evalRunStatusSchema>;

export const evalRunTotalsSchema = z.object({
  cases: z.number().int(),
  samples: z.number().int(),
  passed: z.number().int(),
  passRate: z.number(),
  costUsd: z.string(),
  latencyP50Ms: z.number(),
  latencyP95Ms: z.number(),
  /** False as soon as one case opted into live tools — a run mixing live catalogue data is not a reproducible artifact (PLAN.md test 15). */
  reproducible: z.boolean(),
});
export type EvalRunTotals = z.infer<typeof evalRunTotalsSchema>;

/**
 * The immutable experiment artifact (PLAN.md "A run is an immutable
 * experiment artifact"): what the run *resolved to*, never what it was
 * *asked for*, because inputs are intent and intent isn't reproducible.
 */
export const evalRunSnapshotSchema = z.object({
  provider: z.string(),
  resolvedModel: z.string(),
  modelParameters: z.record(z.unknown()),
  systemPromptHash: z.string(),
  toolFixtureVersion: z.string(),
  graderVersion: z.string(),
  judgeModel: z.string().nullable(),
  judgePromptVersion: z.string(),
  runtimeCommit: z.string().nullable(),
});
export type EvalRunSnapshot = z.infer<typeof evalRunSnapshotSchema>;

export const evalRunDtoSchema = z.object({
  id: z.string().uuid(),
  suiteId: z.string().uuid(),
  botVersionId: z.string().uuid(),
  /** Which pointer the version was under when the run started — what makes a draft-vs-published diff possible. */
  versionRole: z.enum(["draft", "published", "other"]),
  botModelOverride: z.string().nullable(),
  samplesN: z.number().int(),
  passThreshold: z.number(),
  temperature: z.number(),
  status: evalRunStatusSchema,
  totals: evalRunTotalsSchema.nullable(),
  snapshot: evalRunSnapshotSchema,
  error: z.string().nullable(),
  triggeredBy: z.string().uuid().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type EvalRunDto = z.infer<typeof evalRunDtoSchema>;

export const evalResultDtoSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  caseId: z.string().uuid(),
  caseKey: z.string().uuid(),
  caseName: z.string(),
  sampleIdx: z.number().int(),
  passed: z.boolean(),
  graderResults: z.array(graderResultSchema),
  transcript: evalTranscriptSchema,
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  costUsd: z.string(),
  latencyMs: z.number().int(),
});
export type EvalResultDto = z.infer<typeof evalResultDtoSchema>;

// --- Diff ---------------------------------------------------------------

export const evalCaseDiffSchema = z.object({
  caseKey: z.string().uuid(),
  caseName: z.string(),
  /** `null` when a case exists in only one of the two runs. */
  basePassed: z.boolean().nullable(),
  headPassed: z.boolean().nullable(),
  transition: z.enum(["unchanged_pass", "unchanged_fail", "fixed", "regressed", "added", "removed"]),
  basePassRate: z.number().nullable(),
  headPassRate: z.number().nullable(),
  costDeltaUsd: z.string().nullable(),
  latencyDeltaMs: z.number().nullable(),
});
export type EvalCaseDiff = z.infer<typeof evalCaseDiffSchema>;

/**
 * A comparison that can **refuse to compare** (PLAN.md "A run is an
 * immutable experiment artifact"): if two runs differ in grader or
 * judge-prompt version, the pass-rate delta between them is really a judge
 * change wearing a prompt change's clothes, so the diff says so instead of
 * reporting a number.
 */
export const evalDiffDtoSchema = z.discriminatedUnion("comparable", [
  z.object({
    comparable: z.literal(false),
    reason: z.string(),
    baseRunId: z.string().uuid(),
    headRunId: z.string().uuid(),
  }),
  z.object({
    comparable: z.literal(true),
    baseRunId: z.string().uuid(),
    headRunId: z.string().uuid(),
    cases: z.array(evalCaseDiffSchema),
    regressions: z.array(z.string().uuid()),
    basePassRate: z.number(),
    headPassRate: z.number(),
    costDeltaUsd: z.string(),
    latencyP50DeltaMs: z.number(),
  }),
]);
export type EvalDiffDto = z.infer<typeof evalDiffDtoSchema>;

// --- Request bodies -----------------------------------------------------

export const createEvalSuiteBodySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
});
export type CreateEvalSuiteBody = z.infer<typeof createEvalSuiteBodySchema>;

export const evalCaseBodySchema = z.object({
  name: z.string().min(1).max(160),
  tags: z.array(z.string().min(1).max(40)).max(20).default([]),
  turns: z.array(evalTurnSchema).min(1).max(20),
  fixtures: evalFixturesSchema.default({ calls: [] }),
  graders: z.array(graderSchema).min(1).max(20),
  weight: z.number().positive().max(100).default(1),
  /** Opting a case into live tools is explicit and costs it its reproducibility (PLAN.md test 15). */
  useLiveTools: z.boolean().default(false),
});
export type EvalCaseBody = z.infer<typeof evalCaseBodySchema>;

export const seedEvalCaseBodySchema = z.object({
  conversationId: z.string().uuid(),
  name: z.string().min(1).max(160).optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).default([]),
  graders: z.array(graderSchema).max(20).default([]),
});
export type SeedEvalCaseBody = z.infer<typeof seedEvalCaseBodySchema>;

export const startEvalRunBodySchema = z.object({
  /** Which version to grade. Defaults to the draft — the thing being changed is the thing worth grading. */
  version: z.enum(["draft", "published"]).default("draft"),
  botModelOverride: z.string().min(1).max(120).optional(),
  judgeModel: z.string().min(1).max(120).optional(),
  samplesN: z.number().int().min(1).max(10).default(3),
  /** Fraction of samples that must pass for the case to count as passed — 2/3 by default (PLAN.md "Handling non-determinism"). */
  passThreshold: z.number().min(0).max(1).default(2 / 3),
});
export type StartEvalRunBody = z.infer<typeof startEvalRunBodySchema>;

/** The regression that blocked a publish, as the panel renders it. */
export const evalGateRegressionSchema = z.object({
  caseKey: z.string().uuid(),
  caseName: z.string(),
});
export type EvalGateRegression = z.infer<typeof evalGateRegressionSchema>;

export const evalGateBlockedResponseSchema = z.object({
  error: z.literal("eval_regression"),
  baseRunId: z.string().uuid(),
  headRunId: z.string().uuid(),
  regressions: z.array(evalGateRegressionSchema),
});
export type EvalGateBlockedResponse = z.infer<typeof evalGateBlockedResponseSchema>;

export const publishBotBodySchema = z.object({
  /** Publishing over a failing eval diff is allowed, never silent — the override lands in `audit_log` (PLAN.md "Publishing gate", test 16). */
  overrideEvalGate: z.boolean().default(false),
  overrideReason: z.string().min(1).max(500).optional(),
});
export type PublishBotBody = z.infer<typeof publishBotBodySchema>;
